/**
 * Le contexte offert aux gabarits — la liste blanche des sources.
 *
 * Un gabarit ne peut lire QUE ce que cet objet lui présente. Il n'y a pas de
 * chemin depuis un champ éditable vers une requête arbitraire : les sources
 * sont assemblées ici, en JavaScript, à partir des services existants.
 *
 * DEUX APPELS, PAS VINGT ET UN. Une page peut porter cinquante expressions
 * visant les modules et les tarifs ; elles lisent toutes le même contexte,
 * chargé une fois avant le rendu. Résoudre chaque expression par un appel
 * réseau rendrait le moteur inutilisable.
 *
 * LES CHAMPS SONT DÉJÀ TRADUITS. L'API rend toutes les langues d'un coup
 * (name_fr, name_nl, …) ; le contexte choisit la bonne colonne et expose un
 * simple `nom`. Un gabarit n'a donc jamais à écrire « {{ m.name_nl }} », ce
 * qui le rendrait faux dans les sept autres langues.
 */

import { getModules } from '../services/modulesService';
import { getAllTarifs } from '../services/tarifsService';

/** La seule langue obligatoire, cf. cabaza_pages.cfc. */
const LANGUE_REPLI = 'fr';

/**
 * Lit un champ traduit : `champ_nl`, puis `champ_fr`, puis `champ`.
 *
 * Les clés sont cherchées en minuscules ET en camelCase : queryToArray rend
 * du minuscule, mais certaines réponses remontent autrement.
 */
function lireTraduit(objet, base, langue) {
    if (!objet) return '';

    const candidats = [
        `${base}_${langue}`,
        `${base}_${LANGUE_REPLI}`,
        base
    ];

    for (const c of candidats) {
        for (const cle of [c, c.toLowerCase(), c.toUpperCase()]) {
            const v = objet[cle];
            if (v !== null && v !== undefined && String(v).trim() !== '') {
                return String(v);
            }
        }
    }

    return '';
}

/** Lit une clé dans les trois casses. */
function lire(objet, cle) {
    if (!objet) return undefined;
    if (cle in objet) return objet[cle];
    const bas = cle.toLowerCase();
    if (bas in objet) return objet[bas];
    const haut = cle.toUpperCase();
    if (haut in objet) return objet[haut];
    return undefined;
}

/**
 * Met un module en forme pour les gabarits.
 *
 * Les noms de champs sont en français, comme le vocabulaire des blocs
 * (`titre`, `sousTitre`, `cartes`) : un auteur de page n'a pas à connaître
 * le schéma de la base.
 */
export function normaliserModule(brut, langue) {
    const code = String(lire(brut, 'code') || '');

    return {
        code,
        nom: lireTraduit(brut, 'name', langue),
        resume: lireTraduit(brut, 'shortdescription', langue),
        description: lireTraduit(brut, 'description', langue),
        icone: String(lire(brut, 'icon') || ''),
        couleur: String(lire(brut, 'color') || ''),
        ordre: Number(lire(brut, 'sortorder')) || 0,
        // Le lien vers la page de présentation. Construit ici et non dans le
        // gabarit : une URL bâtie par concaténation dans un champ éditable
        // serait une porte ouverte.
        lien: code ? `/module/${code}` : ''
    };
}

/**
 * Met un tarif en forme.
 *
 * Le prix reste un NOMBRE, non formaté : c'est le filtre `monnaie` qui le
 * met en forme selon la langue du lecteur. Formater ici figerait le format
 * français pour les huit langues.
 */
export function normaliserTarif(brut, langue) {
    const code = String(lire(brut, 'code') || '');

    // Le prix peut manquer : on laisse alors undefined plutôt que zéro, pour
    // que `monnaie` rende une chaîne vide et non « 0,00 € ».
    let prix = lire(brut, 'monthlyprice');
    if (prix === null || prix === '') prix = undefined;

    let prixAnnuel = lire(brut, 'yearlyprice');
    if (prixAnnuel === null || prixAnnuel === '') prixAnnuel = undefined;

    return {
        code,
        nom: lireTraduit(brut, 'name', langue),
        description: lireTraduit(brut, 'description', langue),
        prix,
        prixAnnuel
    };
}

/**
 * Construit le contexte complet.
 *
 * Rend un objet prêt pour `rendre()` :
 *
 *   modules          la liste, triée par sortorder
 *   module.BOOKS     un module par son code
 *   tarifs           la liste
 *   tarif.STARTER    un tarif par son code
 *   site.nbModules   le nombre de modules actifs
 *   page.*           ce que l'appelant fournit (titre, dateMaj…)
 *
 * N'échoue jamais : un service en erreur rend une liste vide, et les
 * gabarits qui en dépendent rendent du vide. Une page amputée vaut mieux
 * qu'une page blanche.
 */
export async function construireContexte(langue = LANGUE_REPLI, page = {}) {
    const [modulesBruts, tarifsBruts] = await Promise.all([
        getModules(langue).catch(() => []),
        getAllTarifs().catch(() => [])
    ]);

    const modules = (Array.isArray(modulesBruts) ? modulesBruts : [])
        .map((m) => normaliserModule(m, langue))
        .filter((m) => m.code)
        .sort((a, b) => a.ordre - b.ordre);

    const tarifs = (Array.isArray(tarifsBruts) ? tarifsBruts : [])
        .map((t) => normaliserTarif(t, langue))
        .filter((t) => t.code);

    // Accès par code : « {{ tarif.BOOKS.prix }} » plutôt qu'une boucle.
    const parCodeModule = {};
    modules.forEach((m) => { parCodeModule[m.code] = m; });

    const parCodeTarif = {};
    tarifs.forEach((t) => { parCodeTarif[t.code] = t; });

    return {
        modules,
        module: parCodeModule,
        tarifs,
        tarif: parCodeTarif,
        site: {
            // Compté en base : ajouter un module met à jour toutes les pages
            // qui annoncent le nombre. Le revers est assumé — désactiver un
            // module fera passer « 21 » à « 20 » partout, ce qui est exact.
            nbModules: modules.length,
            nbLangues: 8
        },
        page: page || {}
    };
}
